import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {FocusGuards, FocusScope} from 'radix-ui/internal'
import {RemoveScrollBar} from 'react-remove-scroll-bar'

import {
  type DocumentMaterial,
  type DocumentMaterialRef,
  type DocumentMaterialState,
  type DocumentPageMaterial,
  materialPageCounts,
  pageMaterialFor,
  totalsOf,
} from '#/lib/document/material'
import {type PageAxis, readPageTouch} from '#/lib/document/pageTouch'
import {type PdfSource} from '#/lib/document/pdf.web'
import {fitPageColumn} from '#/lib/document/pdfFit'
import {
  type DocumentAudience,
  type DocumentEmbedView,
} from '#/lib/document/types'
import {useDraftHold} from '#/lib/document/useDraftHold'
import {usePageReport} from '#/lib/document/usePageReport'
import {usePdfDocument} from '#/lib/document/usePdfDocument.web'
import {buildDocumentBlobUrl} from '#/lib/document/util'
import {getPdfDpr} from '#/lib/document/webEnv.web'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {isTextEntry} from '#/lib/textEntry'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import * as Dialog from '#/components/Dialog'
import {DocumentAudienceLine} from '#/components/documents/DocumentAudience'
import {useDocumentChrome} from '#/components/documents/documentChrome'
import {
  MaterialFailedCard,
  MaterialLoadingCard,
  PageAudioControl,
  PageQuestionsCard,
} from '#/components/documents/DocumentMaterialPlay'
import {
  PageMaterialBar,
  PageMaterialEditor,
} from '#/components/documents/PageMaterialSheet'
import {ArrowsDiagonalIn_Stroke2_Corner0_Rounded as CollapseIcon} from '#/components/icons/ArrowsDiagonal'
import {
  ChevronLeft_Stroke2_Corner0_Rounded as ChevronLeftIcon,
  ChevronRight_Stroke2_Corner0_Rounded as ChevronRightIcon,
  ChevronTopBottom_Stroke2_Corner0_Rounded as ChevronVerticalIcon,
} from '#/components/icons/Chevron'
import {PlusLarge_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {Loader} from '#/components/Loader'
import {
  Outlet as PortalOutlet,
  Portal,
  Provider as PortalProvider,
} from '#/components/Portal'
import {StudioConfirmDialog} from '#/components/studio/StudioConfirmDialog'
import {Text} from '#/components/Typography'
import {type FlashBookPageResult} from '#/types/flashbook'
import {ChromeButton, ThumbnailRail} from './reader/ReaderChrome.web'
import {ScrollReader} from './reader/ScrollReader.web'
import {SnapReader} from './reader/SnapReader.web'
import {useQuizDoors} from './reader/useQuizDoors'
const ZOOM_MIN = 0.5
const ZOOM_MAX = 4
const ZOOM_STEP = 1.25

/**
 * What a touch has to land on for the touch to belong to that thing.
 *
 * The page is a control in this reader, but it is not the only one on screen: a
 * press that started on a button, a link or a slider is that control's, and reading
 * it as a flick or a tap would press nothing and turn a page instead. Fields are
 * asked about separately, by `isTextEntry` — the same question the keyboard asks
 * before it claims a key — because a press inside one is the caret's.
 */
const CONTROL_SELECTOR = [
  'button',
  'a',
  'select',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="slider"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(', ')

/** Whether the element a touch started on is something that answers for itself. */
function touchedControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(CONTROL_SELECTOR) !== null
}

/**
 * The film a control is drawn in when it lands on paper rather than on chrome.
 *
 * The same value the page's clip and the players strip use for the same reason:
 * a page is white in both modes, so a control over one has to be dark in both.
 */
const PAGE_FILM = 'rgba(0, 0, 0, 0.55)'
/**
 * The rim a control over a page wears, where it may hang over the canvas.
 *
 * The stage holds a page *fitted* to it, not one stretched into it, so the corner
 * a control is drawn in is paper when the page binds that dimension and the
 * reader's canvas when it does not — and the canvas follows the app's mode (a pale
 * desk in light, a dark one in dark). Paper alone is contrast enough for the film
 * (which is why the page's clip needs no edge), but a film over the *dark* canvas
 * is a dark thing on a dark thing — the part of the circle that lands there would
 * read as nothing. A hairline of light costs nothing on paper (white on white is
 * white) and draws the whole circle back on the other ground.
 */
const PAGE_RIM = 'rgba(255, 255, 255, 0.28)'

/**
 * The author's `+`, drawn larger than the reader's own controls.
 *
 * It is the one press that writes the document, and it is pressed page after page,
 * where every other control in here is pressed once and forgotten. So it is a target
 * rather than a control: bigger than the zoom buttons beside it, on the film every
 * control over a page wears (the page is white paper in both modes), with the rim that
 * keeps it from dissolving into a dark canvas.
 */
const ADD_ACTIVITY_BUTTON = {
  width: 52,
  height: 52,
  borderRadius: 99,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  backgroundColor: PAGE_FILM,
  borderWidth: 1,
  borderColor: PAGE_RIM,
  cursor: 'pointer' as const,
}

/**
 * Which of the reader's two grounds a control is drawn on.
 *
 * The reader has two grounds and they are not interchangeable. Its chrome — the
 * header, the footer, the rail — follows the app's mode, because it is the app's
 * furniture around the paper. The paper does not: a page is white whether the app
 * is dark or light, so a control laid over one has to be too, or it vanishes the
 * moment the page does not. This type is how a control says which ground it is
 * on.
 */

export type PdfFullscreenProps = {
  embed: DocumentEmbedView
  authorDid: string
  preview?: {cacheKey: string; bytes: Uint8Array}
  initialPage?: number
  numPagesHint?: number
  /** Who the document was published for, named in the reader's own language. */
  audience?: DocumentAudience
  onClose: () => void
  /**
   * The document's material, when the reader's surface holds any.
   *
   * Asked for by *count* on every turn and by *content* only once something is
   * played: the page table comes off the post, and the record behind it — the
   * clips and the questions themselves — is fetched by `onRequestMaterial` at
   * the first press. This is the only place a document's questions are answered
   * and its clips are heard; the carousel outside shows what a page holds and
   * hands the reader here to do anything about it.
   */
  material?: DocumentMaterial
  /**
   * What the fetch has to say about that material.
   *
   * Absent material and *no* material are the same value to this surface and
   * opposite meanings (see `DocumentMaterialState`), and the difference decides
   * two things here: whether a page's questions are worth waiting for, and
   * whether a document that says it asks something can be read past at all.
   */
  materialState: DocumentMaterialState
  /** What each page holds, from the post, so a turn needs no fetch. */
  pageTable?: DocumentMaterialRef
  /**
   * Called on the first request for material: this is what starts the fetch.
   *
   * The reader asks on its own facts and never on this function's identity, so a
   * caller that writes it as an arrow in its own body — a new function on every one
   * of its renders — is not a caller asking again (`useMaterialRequest`).
   */
  onRequestMaterial?: () => void
  /**
   * Ask for that material again, after a read that did not get through.
   *
   * Deliberately not `onRequestMaterial`: that one is the *automatic* ask, keyed on
   * facts about the reader (the open, a card already up) and counted, so that a
   * caller which re-renders is not a caller asking again. A failure is the one case
   * where asking is a reader's decision — the record is still there, the wire was
   * not — so it is a press, made once per press, and absent means this reader has
   * nothing to offer when a read fails (`MaterialFailedCard` is then drawn without
   * its button rather than with a button that does nothing).
   */
  onRetryMaterial?: () => void
  /**
   * A page whose questions are open *on arrival* rather than behind the turn.
   *
   * The author's path, and only ever that: the composer's page bar previews the
   * questions that were just written, and there is nothing to read before them.
   * A reader who pressed Play is taken to the page instead and meets its
   * questions on the way out of it, exactly as they meet any other page's.
   */
  startQuizPage?: number
  /**
   * The reader is the author, and nothing is recorded.
   *
   * The composer's own carousel opens this reader to *preview* the questions, so           * the card has to say so rather than quietly scoring a question the author wrote
   * five seconds ago.
   */

  isPreview?: boolean
  /**
   * A page's questions finished. The result travels out rather than up: the read
   * in progress belongs to the whole document (see `#/lib/document/report`), and
   * the surface that owns it is the one that was there before this opened.
   */
  onQuizFinished?: (result: {
    pageResults: FlashBookPageResult
    score: number
    maxPoints: number
  }) => void
  /**
   * A page has come into view — including the one the reader opened on.
   *
   * A reader's *path* leaves this surface the same way their answers do, and for the
   * same reason: it belongs to the read, which is older than this reader and outlives
   * it (`#/lib/document/report`). It is how a read knows where it stopped, which is a
   * fact no submission holds — a page that asks nothing is answered by nothing, and
   * most of a document asks nothing.
   */
  onPageSeen?: (page: number) => void
  /**
   * Commits one page's material into the document being written.
   *
   * The composer's own door, and the *only* way this reader is a writing surface: with
   * it, this is an author's document and the reader grows a `+` for the page in front
   * of them and a bar counting what the page holds; without it, this is a reader of a
   * published document, who is offered neither.
   *
   * It is here rather than in the card because writing a page's audio and questions
   * needs the page: a landscape page drawn at feed width is a strip, and the sheet that
   * writes for it covers the whole of it. Full screen is the one place where the page
   * the questions are about is legible while they are written.
   */
  onChangePageMaterial?: (
    page: number,
    material: DocumentPageMaterial | undefined,
  ) => void
  /**
   * The page an author asked to edit on the way in, when they pressed *Add activity* or
   * *Edit* on a page of the card.
   *
   * Read once, when the surface mounts, like `startQuizPage`: it is how the writer gets
   * here, not a mode the reader holds afterwards.
   */
  startEditorPage?: number
}

/**
 * Full screen document reader, built on the same chrome as the image lightbox
 * (backdrop, focus trap, scroll lock) so it feels like the rest of the app.
 *
 * Two ways to read: a paged view with arrows and zoom, or a continuous scroll
 * for long documents. Either way pages render lazily and at the resolution
 * they are displayed at, so zooming in stays sharp.
 */
export function PdfFullscreen(props: PdfFullscreenProps) {
  // Mounted through the app-level portal outlet on purpose. A carousel can sit
  // inside a card that clips its overflow or establishes a containing block (the
  // composer's live preview is both), and a `position: fixed` overlay inside one
  // of those gets boxed into the card instead of covering the viewport — the
  // reader then nests inside the very preview that opened it and its controls
  // land on whatever is underneath. Portaling keeps this one full screen surface
  // no matter which carousel opened it.
  return (
    <Portal>
      <PdfFullscreenSurface {...props} />
    </Portal>
  )
}

function PdfFullscreenSurface(props: PdfFullscreenProps) {
  FocusGuards.useFocusGuards()
  /*
   * No theme override. The reader used to pin itself to the app's dark theme,
   * because everything it draws — the page, the controls, the questions — was
   * designed against a black ground; the result was a surface that ignored the
   * mode the reader had chosen for the app around it, and a black question card
   * over a white document. The reader now wears the app's own mode, and the
   * values that used to be literals live in one vocabulary
   * (`useDocumentChrome`), because the reader's furniture is the app's.
   *
   * The page itself is the one thing that does not change with the mode: a PDF
   * page is white paper in both, which is why the controls drawn *over* it (in
   * the card) keep their dark scrim there.
   */
  return (
    <View style={[a.fixed, a.inset_0, a.z_10]}>
      <RemoveScrollBar />
      <ReaderBackdrop />
      {/*
        The reader, its own portal outlet, then the outlet itself — in that
        order, so anything opened from in here is drawn *after* the page rather
        than under the backdrop.

        This is the app's own portal group, nested. A dialog goes to the outlet
        that is nearest to it, and the outermost one is where this reader lives:
        a board opened from the strip would be an entry *beside* the reader, at
        the same z-index and appended earlier, so it would paint underneath the
        very surface it was opened from (measured: the reader's own backdrop is
        what answers a click where the board is). Nesting the group puts the
        board inside this surface instead — and a private group would not do,
        since the app's dialogs append to this one.

        The focus trap closes before the outlet on purpose: a board opened from
        the strip is a modal of its own, and it cannot be reached by keyboard if
        this surface pulls focus back to the page every time it leaves.

        The scope owns its element rather than borrowing the reader's (`asChild`).
        A borrowed element has to hand the scope a ref, and the reader's own
        component does not — which left this trap with no container at all: it
        never registered on Radix's scope stack, so it never *paused* the dialog
        underneath. The result was a reader opened from the composer whose every
        control gave focus straight back to the card behind it (measured: a click
        on the page's question field focused the card's own *Edit* button), which
        is to say the author could not type a question at all. A plain div in
        between changes nothing on screen: the reader draws itself `absolute;
        inset: 0`, so the wrapper is not a containing block and the page still
        fills the window.
      */}
      <PortalProvider>
        <FocusScope.FocusScope loop trapped>
          <PdfFullscreenInner {...props} />
        </FocusScope.FocusScope>
        <PortalOutlet />
      </PortalProvider>
    </View>
  )
}

/**
 * Fully opaque, unlike the 80%-black scrim the image lightbox uses. A reader
 * that lets the post (or the composer's own carousel) show through reads as a
 * preview stacked on a preview, and the visible controls underneath invite
 * clicks that never reach them. Documents get one surface and nothing else.
 *
 * Opaque is the only thing that does not change with the app's mode: the canvas
 * itself does (`useDocumentChrome`), because a reader in light mode is a white
 * page on a pale grey desk rather than a lit sheet on black.
 */
function ReaderBackdrop() {
  const chrome = useDocumentChrome()
  return <View style={[a.fixed, a.inset_0, {backgroundColor: chrome.canvas}]} />
}

function PdfFullscreenInner({
  embed,
  authorDid,
  preview,
  initialPage = 1,
  numPagesHint,
  audience,
  onClose,
  material,
  pageTable,
  onRequestMaterial,
  onRetryMaterial,
  startQuizPage,
  isPreview = false,
  materialState,
  onQuizFinished,
  onPageSeen,
  onChangePageMaterial,
  startEditorPage,
}: PdfFullscreenProps) {
  const {_} = useLingui()
  const t = useTheme()
  const chrome = useDocumentChrome()
  const {gtTablet} = useBreakpoints()
  const {currentAccount} = useSession()
  /**
   * The display the reader draws: the pages, paged.
   *
   * A second display — the continuous one, still drawn below — used to be offered
   * here as a header button, and the button has gone (24 Sep 2026). It sat between
   * the two controls that *move* the reader (which way the pages run, and how far
   * into them the reader is) and changed what a page *is*, which is not a thing to
   * put in front of somebody mid-document.
   *
   * Which is why this is a constant and no longer the `useState` it was: a state
   * nothing can set is a state in name only, while the branches below want a value
   * of the union either way (and the continuous reader, its door and its arithmetic
   * keep compiling because of it). A way back in is a press that calls `setMode` —
   * a setting, a gesture or a reader preference rather than chrome beside the arrow
   * — and that press turns this line back into the `useState` and changes nothing
   * else.
   */
  const mode: 'paged' | 'scroll' = 'paged'
  /**
   * Which way the pages run — the reader's own choice, and a setting of both
   * displays rather than of one.
   *
   * The paged view lays its pages in a column or a row and the continuous one does
   * the same, so "read down" and "read across" mean the same thing whichever display
   * the reader has chosen, and one gesture rule follows from it (`pageTouch`): the
   * pages are carried along their own axis and turned by a flick across it. Down is
   * the default because a document is a stack of pages, not a filmstrip.
   */
  const [axis, setAxis] = useState<PageAxis>('y')
  const [page, setPage] = useState(initialPage)
  const [zoom, setZoom] = useState(1)
  const [bodySize, setBodySize] = useState({width: 0, height: 0})
  /**
   * The reader's furniture, and whether the reader has asked for it.
   *
   * A page is what the reader came for and the header and the bar are the app's way
   * around it — both about the reader rather than about the paper, and neither of
   * them a document. So a tap puts them away, and a tap brings them back: a reader
   * who cannot find the way out of a page has been handed a worse reader, not a
   * quieter one. Swipes keep working while they are gone, because the gestures are
   * the *page's* — the bars were never what turned it.
   *
   * Hidden means *not drawn*, rather than drawn transparent: the body then takes the
   * space, so what the reader gains is not only a clean page but a larger one, and
   * the page's own box already eases to its new size (`PagedReader`). What must not
   * happen is the page moving under a reader who only wanted the buttons gone.
   */
  const [chromeHidden, setChromeHidden] = useState(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const dpr = getPdfDpr()

  const source = useMemo<PdfSource>(() => {
    if (preview) {
      return {kind: 'bytes', key: preview.cacheKey, bytes: preview.bytes}
    }
    const headers: Record<string, string> = {}
    if (currentAccount?.accessJwt) {
      headers.Authorization = `Bearer ${currentAccount.accessJwt}`
    }
    return {
      kind: 'remote',
      key: `${authorDid}:${embed.cid}`,
      url: buildDocumentBlobUrl(authorDid, embed.cid),
      headers,
    }
  }, [preview, currentAccount, authorDid, embed.cid])

  const {status, doc, docKey, numPages, ratio, retry} = usePdfDocument({
    source,
    enabled: true,
  })

  /**
   * The column the author's card lives in: the paper's own width, not the reader's.
   *
   * A card drawn over a page is *about* that page — its audio and its questions —
   * so it takes the page's edges and sits where the page sits. That is the card a
   * phone gets for free (there the paper is fitted to the screen, so the two widths
   * are one number), and the card this reader was missing: on a laptop in full
   * screen the paper is 450 wide and the reader 1280, and the card took the reader.
   *
   * The same fit the pages are laid out with (`SnapReader` calls `fitPagedPage`
   * with exactly these numbers), read here through `fitPageColumn` rather than
   * guessed, so the card tracks a window that is resized and a page that is zoomed.
   */
  const pageColumn = useMemo(
    () =>
      fitPageColumn({
        width: bodySize.width,
        height: bodySize.height,
        ratio,
        zoom,
      }),
    [bodySize.height, bodySize.width, ratio, zoom],
  )

  /**
   * The deliberate ask, kept at one identity for the same reason the automatic one is
   * (`useMaterialRequest`): the card's controls are props of a surface the caller re-renders,
   * and a control handed a new function on every render of a live feed card is a control whose
   * own render is not its own. Never called from an effect — only from a press.
   */
  const retryMaterial = useNonReactiveCallback(onRetryMaterial)

  /*
   * The caller's own functions, erased at the door like `retryMaterial` above.
   *
   * A callback prop arrives with an identity whoever rendered the parent chose — a
   * parent card re-renders for a like landing, or for a keystroke in a field — so a
   * component that names one in a memo's dependencies is re-made whenever that
   * happens, and everything below it re-renders with it. Neither of these two doors
   * changes what it *does* when the caller hands over a new function: the way out is
   * still the way out, and a finished page is still reported. What changes is that
   * the reader's own renders are its own, and that the surface a live card draws does
   * not re-render the whole document with it (`useNonReactiveCallback`; the rule that
   * keeps this true is `bsky-internal/no-reactive-prop-deps`).
   */
  const closeReader = useNonReactiveCallback(onClose)
  const quizFinished = useNonReactiveCallback(onQuizFinished)

  const totalPages = status === 'ready' ? numPages : numPagesHint || 1

  /**
   * The page whose audio and questions are being written, if any.
   *
   * The author is the only reader who can have this, and `onChangePageMaterial` is what
   * says so — the same test the card makes (see its `isComposing`), so the two surfaces
   * cannot disagree about who is writing.
   */
  const isWriting = !!onChangePageMaterial

  /**
   * The page's card, the draft it holds, and the doors that draft holds up.
   *
   * The whole of the unsaved-page guard is `#/lib/document/useDraftHold`, because the
   * asking is one rule rather than one rule per control: every door the reader has (a turn,
   * a jump, a scroll, the way out) calls `holdForDraft`, does nothing when it answers true,
   * and lets `discardDraft` take the deed it held. The dialog control is the reader's — the
   * app's own confirm is drawn *here*, over the page the card is writing — and the hook is
   * given it so that asking stays that dialog.
   */
  const discardControl = Dialog.useDialogControl()
  const {
    editorPage,
    setEditorDirty,
    openEditor,
    closeEditor,
    holdForDraft,
    discardDraft,
  } = useDraftHold({page, startEditorPage, discardControl})

  /*
   * What the document holds, asked of the *table* rather than of the material: the table
   * is on the post, so this needs no fetch and is right on the first paint. It is the
   * reader's own summary of what there is to play, and it is what tells an ask for
   * material whether there is anything to ask *for*.
   */
  const documentTotals = totalsOf(pageTable)
  const documentCarries =
    documentTotals.questions > 0 || documentTotals.audio > 0

  /*
   * The doors: the pages a reader may leave, and the questions that hold them. Every
   * way between pages is in there — the bar's presses, the keys, a flick, a jump, a
   * scroll — and so is the way out, because all of them ask the same unsaved-page
   * question first (`./reader/useQuizDoors`).
   */
  const {
    quizPage,
    setQuizPage,
    quizQuestions,
    askForMaterial,
    requestPage,
    stepBack,
    stepForward,
    onScrollPage,
    closeQuiz,
    requestClose,
    canStepForward,
    forwardLabel,
    carriedOnTo,
  } = useQuizDoors({
    page,
    setPage,
    totalPages,
    paged: mode === 'paged',
    /*
     * A turn is a door for a *reader*, not for an author.
     *
     * The rule this reader is built on — a page's questions are the way out of it —
     * exists so nobody is carried past a page they have not read or been asked about.
     * An author is not being carried anywhere: the questions on the page they are
     * leaving are the ones they just wrote, so a turn that opened them would be this
     * surface interrupting them with their own work on the way to writing the next
     * page. In their reader the questions are raised by the page bar's *Preview*
     * instead (below), which is a press rather than a turn — and every walk here
     * behaves as though no page asked anything, so the arrow says `Next page`
     * wherever it would otherwise say `Answer to carry on`.
     */
    asksOnTurn: !isWriting,
    pageTable,
    material,
    materialState,
    carries: documentCarries,
    startQuizPage,
    onRequestMaterial,
    holdForDraft,
    closeReader,
    quizFinished,
  })

  /*
   * The page the reader is on, told outwards as it changes.
   *
   * One report on `page` rather than a call in each of the places that can move it
   * (the bar's two presses, PageDown, a flick, a jump from the thumbnail rail), because
   * this is a fact about where the reader *is*, not about which control put them
   * there — and a new way of turning a page must not be a way of losing the read.
   *
   * It fires on the page the reader *opened* on too, which is the first page of the
   * path and the one a reader who never turns a page is recorded as having read.
   *
   * The report's *identity* is the page and nothing else: the card that owns the read
   * writes what it hears into its own state and hands this surface a fresh arrow on
   * every one of its renders, so an effect keyed on that arrow re-fires for a page
   * that has not moved — and, on a card whose write re-renders it, that is a loop
   * rather than a duplicate. `usePageReport` is where the page is the only dep.
   */
  usePageReport(page, onPageSeen)

  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  const setZoomClamped = useCallback((next: number) => {
    setZoom(Math.min(Math.max(next, ZOOM_MIN), ZOOM_MAX))
  }, [])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      /*
       * The caret wins. Every key this handler claims is a key an author writing a
       * question also needs: `0`/`-`/`+` are characters, the arrows move the caret,
       * and Escape is the way back out of a field they are still in. Asking only
       * for `isContentEditable` said "not typing" for every studio field, since
       * those are real `<input>`/`<textarea>` elements — so a page's questions
       * could not be written where they are written (see `isTextEntry`).
       */
      if (isTextEntry(event.target)) return
      switch (event.key) {
        case 'Escape':
          event.preventDefault()
          event.stopPropagation()
          /*
           * Escape backs out one layer at a time: the questions first, the reader
           * second. Closing the whole reader from inside a quiz would leave the
           * reader holding a half-answered page with no way back to it.
           *
           * The fourth layer — a page's card, and the question it is being written
           * with — is not skipped by the third: `requestClose` is where a page that
           * has not been confirmed stops the reader from closing on it.
           */
          if (quizPage !== null) closeQuiz()
          else requestClose()
          break
        case 'ArrowRight':
        case 'PageDown':
          if (mode !== 'paged') return
          event.preventDefault()
          // Through the door, not around it.
          stepForward()
          break
        case 'ArrowLeft':
        case 'PageUp':
          if (mode !== 'paged') return
          if (quizPage !== null) return
          event.preventDefault()
          requestPage(page - 1)
          break
        /*
         * Both ends of the document, asked the way the rail's pages are: the door is
         * per *crossing*, so Home (which crosses nothing forward) arrives at once and
         * End stops at the first page that asks on the way to the last one.
         */
        case 'Home':
          event.preventDefault()
          requestPage(1)
          break
        case 'End':
          event.preventDefault()
          requestPage(totalPages)
          break
        case '+':
        case '=':
          event.preventDefault()
          setZoomClamped(zoom * ZOOM_STEP)
          break
        case '-':
        case '_':
          event.preventDefault()
          setZoomClamped(zoom / ZOOM_STEP)
          break
        case '0':
          event.preventDefault()
          setZoom(1)
          break
      }
    },
    [
      closeQuiz,
      mode,
      page,
      quizPage,
      requestClose,
      requestPage,
      setZoomClamped,
      stepForward,
      totalPages,
      zoom,
    ],
  )

  const pageCounts = pageTable
    ? materialPageCounts(pageTable, page)
    : {audio: 0, questions: 0}
  const pageRow = material ? pageMaterialFor(material, page) : undefined

  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      setZoomClamped(zoom * (event.deltaY < 0 ? 1.1 : 1 / 1.1))
    },
    [setZoomClamped, zoom],
  )

  /**
   * Where the last touch started, and whether the page owns it.
   *
   * Both ends of a touch are needed to read it — a gesture is *travel*, not a
   * position — and only the start can say whose it was: what a press began on is
   * decided once, rather than again at the end, when the questions card may have
   * taken its place on screen.
   */
  const touchStart = useRef<{x: number; y: number; byThePage: boolean} | null>(
    null,
  )

  const startTouch = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // The primary press only: a right click is a menu, not a flick.
      if (event.button !== 0) return
      touchStart.current = {
        x: event.clientX,
        y: event.clientY,
        byThePage: !isTextEntry(event.target) && !touchedControl(event.target),
      }
    },
    [],
  )

  /** A touch the browser took away — a cancelled gesture turns nothing. */
  const forgetTouch = useCallback(() => {
    touchStart.current = null
  }, [])

  const endTouch = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const started = touchStart.current
      touchStart.current = null
      if (!started) return
      const touch = readPageTouch({
        dx: event.clientX - started.x,
        dy: event.clientY - started.y,
        paging: axis,
      })
      /*
       * A press that began on a control, or in a field, was never the page's — and
       * the questions card and the author's editor are drawn *over* the page, so
       * their own presses have to be handed back rather than read as turns.
       */
      if (!started.byThePage) return
      if (touch === 'tap') {
        setChromeHidden(hidden => !hidden)
        /*
         * The reader keeps the keys. A control that was on screen when the tap
         * landed may have just gone — the bar's pager, the way out — and a page can
         * lose the keyboard with the button that was holding the focus.
         */
        dialogRef.current?.focus()
        return
      }
      if (touch === 'none') return
      /*
       * The page's own pager, offered exactly as the bar's two presses are.
       *
       * A zoomed page has somewhere to go sideways and a pan is what the reader's
       * hands are doing, so at that size the paper owns the drag and the document
       * does not turn. And while a page's questions are open they own the surface —
       * the same rule the scroll follows — so a flick behind the card cannot move
       * the page out from under the answers being given to it.
       */
      if (zoom > 1 || quizPage !== null) return
      /*
       * In the paged view the *pages* own the flip, because the paper is what answers it
       * there: `SnapReader` drags the page under the hand and settles the turn itself, so
       * a flip read here as well would turn the page twice. Two states are the exception,
       * and both have already stood the pages down — a zoomed page (above) and an open
       * card (`locked`) — so the flip in those goes on asking exactly what the arrow asks,
       * the author's draft first among them.
       */
      if (mode === 'paged' && quizPage === null && editorPage === null) return
      if (touch === 'previous') {
        // Backwards is a page already read, which is why it is never held up.
        if (page > 1) requestPage(page - 1)
        return
      }
      /*
       * Forward is the *door*, not a page number incremented: `stepForward` is what
       * asks a page's questions before letting the reader past them, so a flick that
       * went round it would make answering optional — and the questions on the next
       * page would be the one thing a reader could not reach by the gesture they
       * reach with. It is also the paged reader's step, so in the scroll the flick
       * stands down with the forward arrow: there, the scroll is the way on and a
       * page's questions are met by arriving at them.
       */
      if (canStepForward) stepForward()
    },
    [
      axis,
      canStepForward,
      editorPage,
      mode,
      page,
      quizPage,
      requestPage,
      stepForward,
      zoom,
    ],
  )

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={_(msg`Document viewer`)}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onWheel={onWheel}
      onPointerDown={startTouch}
      onPointerUp={endTouch}
      onPointerCancel={forgetTouch}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
      }}>
      {/*
        The first half of the reader's furniture: away on a tap, space and all — so a
        reader who asked for the buttons to go gets a larger page rather than a page
        with holes above and below it (`chromeHidden`).
      */}
      {!chromeHidden && (
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.gap_sm,
            a.px_lg,
            a.py_md,
            {borderBottomWidth: 1, borderBottomColor: chrome.hairline},
          ]}>
          <View style={[a.flex_1, {gap: 2}]}>
            <Text
              style={[a.text_md, a.font_bold, t.atoms.text]}
              numberOfLines={1}>
              {embed.title ?? _(msg`Document`)}
            </Text>
            {/*
              What the document *is*: its audience, beside the subject and the level.

              Where the reader is used to stand here too, in the card's own counter
              — and it has gone back down to the bar, where the two presses that move
              it are. Position and the presses that change it are one thing on
              screen, and the header is left saying what the document is rather than
              how far into it somebody has read.
            */}
            {audience && <DocumentAudienceLine audience={audience} />}
          </View>

          {/*
            Which way the pages run: a column or a row.

            The icon is the direction a press *gives*: a pair of chevrons pointing
            across while the pages are stacked, and one pointing up and down while they
            are in a row. These two presses are the ones that move the reader — the
            display itself is not a choice this header offers (`mode` above).
          */}
          <ChromeButton
            label={
              axis === 'y'
                ? _(msg`Read across the pages`)
                : _(msg`Read down the pages`)
            }
            onPress={() => setAxis(current => (current === 'y' ? 'x' : 'y'))}>
            {axis === 'y' ? (
              <View
                style={[
                  a.flex_row,
                  a.align_center,
                  {marginLeft: -4, marginRight: -4},
                ]}>
                <ChevronLeftIcon size="sm" fill={chrome.ink} />
                <ChevronRightIcon size="sm" fill={chrome.ink} />
              </View>
            ) : (
              <ChevronVerticalIcon size="md" fill={chrome.ink} />
            )}
          </ChromeButton>

          <ChromeButton label={_(msg`Exit full screen`)} onPress={requestClose}>
            <CollapseIcon size="md" fill={chrome.ink} />
          </ChromeButton>
        </View>
      )}

      <View style={[a.flex_1, a.flex_row]}>
        {gtTablet && totalPages > 1 && doc && status === 'ready' && (
          <ThumbnailRail
            doc={doc}
            docKey={docKey}
            ratio={ratio}
            totalPages={totalPages}
            currentPage={page}
            onSelect={requestPage}
          />
        )}

        <View
          style={[a.flex_1]}
          onLayout={event =>
            setBodySize({
              width: event.nativeEvent.layout.width,
              height: event.nativeEvent.layout.height,
            })
          }>
          {status === 'loading' && (
            <View style={[a.flex_1, a.align_center, a.justify_center]}>
              <Loader size="lg" />
              <Text style={[a.mt_md, a.text_sm, t.atoms.text_contrast_medium]}>
                {_(msg`Loading document…`)}
              </Text>
            </View>
          )}

          {status === 'error' && (
            <View
              style={[a.flex_1, a.align_center, a.justify_center, a.gap_md]}>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {_(msg`Could not load this document`)}
              </Text>
              <ChromeButton label={_(msg`Try again`)} onPress={() => retry()}>
                <Text style={[a.text_xs, a.font_bold, {color: chrome.ink}]}>
                  {_(msg`Try again`)}
                </Text>
              </ChromeButton>
            </View>
          )}

          {status === 'ready' && doc && (
            <View style={[a.flex_1]}>
              {mode === 'paged' ? (
                <SnapReader
                  axis={axis}
                  doc={doc}
                  docKey={docKey}
                  page={page}
                  totalPages={totalPages}
                  ratio={ratio}
                  zoom={zoom}
                  dpr={dpr}
                  size={bodySize}
                  locked={quizPage !== null || editorPage !== null}
                  canStepForward={canStepForward}
                  onSwipeNext={stepForward}
                  onSwipePrevious={stepBack}
                />
              ) : (
                <ScrollReader
                  axis={axis}
                  doc={doc}
                  docKey={docKey}
                  page={page}
                  totalPages={totalPages}
                  ratio={ratio}
                  zoom={zoom}
                  dpr={dpr}
                  size={bodySize}
                  pinnedPage={quizPage}
                  onPageChange={onScrollPage}
                />
              )}

              {/*
                The page's clip, at the top of the page it belongs to.

                It used to sit in the reader's header, among the buttons that follow
                the mode — and it wore the reader's chrome there, because a black pill
                in a row of white ones reads as a hole in the row. But a clip is a fact
                about the *paper*, not about the reader's furniture: the card draws it
                in the page's own corner, over the page, in both modes of the app, and
                the reader is the same page. So the reader draws it the way the card
                does — at the top of the page rather than above it — and its header
                keeps only the controls that are about the *reader*: the mode, and the
                way out.

                Right, because that is the corner the card keeps it in, and the corner
                of the page a reader's eye leaves last.

                Drawn on the strength of the page *table*, which is why the control is
                here before its clip is — but not when the material has answered that
                there is none: a clip control whose clip does not exist is a control
                that can never do anything, and it would sit there offering.

                First of the overlays, so that the page's own card — which is the whole
                page when it is up — is drawn over it rather than under it.
              */}
              {pageCounts.audio > 0 && materialState !== 'none' && (
                <View style={[a.absolute, {top: 16, right: 16}]}>
                  <PageAudioControl
                    audioUrl={pageRow?.audioUrl}
                    /*
                     * Pending while the record is *coming*, and not while it has failed: a
                     * control that spins forever over a read that has ended is the same lie
                     * the card refuses to tell. A failed read leaves it pressable, and the
                     * press asks again — which for a clip is the only way in anyway, since
                     * the control's own first press is what starts the fetch.
                     */
                    isPending={
                      materialState === 'asking' || materialState === 'unasked'
                    }
                    onRequest={
                      materialState === 'failed' && onRetryMaterial
                        ? retryMaterial
                        : askForMaterial
                    }
                  />
                </View>
              )}

              {/*
                The questions, over the page they are the door out of. Rendered
                here rather than in the carousel because this is the one surface
                that plays them: outside, a page only says that it asks something.
              */}
              {quizPage !== null && quizPage === page && (
                <View style={[a.absolute, a.inset_0]}>
                  {quizQuestions.length > 0 ? (
                    <PageQuestionsCard
                      /*
                       * Keyed by page, because the card holds the reader's answer
                       * and its verdict: a scroll held up at one page and carried on
                       * to another that asks opens the second card *in the same
                       * update* (both `setQuizPage` calls land in one render), so
                       * without a key React hands the new page the old page's
                       * answer — a page nobody had touched, reading "Correct!".
                       */
                      key={quizPage}
                      page={quizPage}
                      questions={quizQuestions}
                      accent={t.palette.primary_500}
                      isPreview={isPreview}
                      note={
                        carriedOnTo !== undefined
                          ? _(msg`Answer to carry on to page ${carriedOnTo}`)
                          : _(msg`Answer to finish this document`)
                      }
                      onFinish={closeQuiz}
                      onDismiss={() => closeQuiz()}
                    />
                  ) : materialState === 'failed' ? (
                    /*
                     * The questions could not be read at all: the card says so, and asks again
                     * on a press. It is the *reader's* press that retries (`onRetryMaterial`),
                     * never a render of this surface.
                     */
                    <MaterialFailedCard
                      onRetry={onRetryMaterial && retryMaterial}
                    />
                  ) : (
                    <MaterialLoadingCard />
                  )}
                </View>
              )}

              {/*
                What *this page* holds, floating on the page the author is looking at.

                It is the `+`'s other state — "here is what you added" — so the two
                are never both on screen: a page that holds nothing offers the press
                that writes it, and a page that holds something says what, rather
                than asking the author to remember. It is drawn over the page rather
                than in the bar below, because what it counts is the page it is drawn
                on: the bottom bar is the app's furniture around the paper, and this
                is a fact about the paper.

                Absent while the page's own card is up, so it cannot cover the
                answers it is offering to show.
              */}
              {isWriting &&
                editorPage === null &&
                quizPage === null &&
                pageCounts.audio + pageCounts.questions > 0 && (
                  <View style={[a.absolute, {left: 16, bottom: 16}]}>
                    <PageMaterialBar
                      counts={pageCounts}
                      onPreview={() => setQuizPage(page)}
                      onEdit={openEditor}
                    />
                  </View>
                )}

              {/*
                The `+` for a page that holds nothing yet, in the corner opposite.

                It belongs to the *reader* rather than to a bar of its own, because
                writing a page happens wherever the author has stopped: they read, they
                press, they write, they turn the page and press again — and the page
                they are writing for is the one place on screen big enough to write
                against.
              */}
              {isWriting &&
                editorPage === null &&
                pageCounts.audio + pageCounts.questions === 0 && (
                  <View style={[a.absolute, {right: 16, bottom: 16}]}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={_(msg`Add activity to this page`)}
                      accessibilityHint={_(
                        msg`Opens the audio and questions for the page on screen`,
                      )}
                      onPress={openEditor}
                      testID="documentPageAdd"
                      style={({pressed}) => [
                        ADD_ACTIVITY_BUTTON,
                        {opacity: pressed ? 0.75 : 1},
                      ]}>
                      <PlusIcon size="lg" fill="#fff" />
                    </Pressable>
                  </View>
                )}

              {/*
                The page's material, as the card drew it — over the page it is about,
                so that an author reading through what they have written sees the counts
                where they were written rather than in a list somewhere else.

                `Preview` is this surface's own play of the page's questions: the very
                card a reader meets, with `isPreview` telling it that nothing is being
                recorded, which is the only way an author can check what their learners
                will get. In this role it is also the *only* way they are ever raised — a
                turn is a page turn for an author (`asksOnTurn` above), so nothing puts
                them up over the page by itself.
              */}
              {isWriting && editorPage === page && onChangePageMaterial && (
                /*
                 * The card's own frame: a column as wide as the page, centred where
                 * the page is (see `pageColumn`). The card inside it keeps its own
                 * geometry — inset from the column's edges, anchored at its bottom —
                 * so the phone's card and the laptop's card are the same component
                 * measured against different paper.
                 *
                 * `box-none` on both boxes, because the frame is only there to give
                 * the card its edges: a frame that took presses would put the page's
                 * own top corner — its clip, and the reader's furniture above — out
                 * of reach while an author is writing.
                 */
                <View
                  testID="pageMaterialFrame"
                  style={[
                    a.absolute,
                    a.inset_0,
                    a.align_center,
                    {pointerEvents: 'box-none'},
                  ]}>
                  <View
                    testID="pageMaterialColumn"
                    style={[
                      a.flex_1,
                      {
                        width: pageColumn,
                        maxWidth: '100%',
                        pointerEvents: 'box-none',
                      },
                    ]}>
                    <PageMaterialEditor
                      page={page}
                      initial={pageRow}
                      accent={t.palette.primary_500}
                      onConfirm={next => {
                        onChangePageMaterial(page, next)
                        closeEditor()
                      }}
                      onCancel={closeEditor}
                      onDirtyChange={setEditorDirty}
                    />
                  </View>
                </View>
              )}
            </View>
          )}
        </View>
      </View>

      {/*
        The reader's own controls: where they are, and how large the page is.

        The scale stands on the left and the pager on the right, and both are about
        *reading* a page rather than about what is on it — which floats on the paper,
        where it was written. The pager is the far corner because it is what a reader
        reaches for once they have read the page, and the scale is set as they arrive
        and then left alone.

        The arrows used to be drawn over the paper, one in each gutter, and the
        position used to be a chip in the header beside the subject and the level.
        Those are one thing — a position, and the two presses that change it — and
        splitting them across two corners of the screen asked a reader to look in two
        places to page one document. Between the arrows, `3 / 16` reads as what it
        is: the number those two buttons move.

        The author's own furniture has left this bar too. What a page *holds* is
        drawn on the page, and the alt text — which is the *document's*, not a page's
        — is edited where the document is being written, on the card behind this
        reader.

        The bar is the other half of what a tap puts away, with the header, and the
        flick that turns a page works whether it is on screen or not: the gesture is
        the *page's*, and these are only the app's way around it.
      */}
      {!chromeHidden && (
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.justify_between,
            a.gap_sm,
            a.px_lg,
            a.py_sm,
            {borderTopWidth: 1, borderTopColor: chrome.hairline},
          ]}>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <ChromeButton
              label={_(msg`Zoom out`)}
              onPress={() => setZoomClamped(zoom / ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}>
              <Text style={[a.text_md, a.font_bold, {color: chrome.ink}]}>
                −
              </Text>
            </ChromeButton>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={_(msg`Reset zoom`)}
              accessibilityHint=""
              onPress={() => setZoom(1)}
              style={[a.px_xs, a.py_2xs]}>
              <Text style={[a.text_xs, {color: chrome.ink}]}>
                {Math.round(zoom * 100)}%
              </Text>
            </Pressable>
            <ChromeButton
              label={_(msg`Zoom in`)}
              onPress={() => setZoomClamped(zoom * ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}>
              <PlusIcon size="md" fill={chrome.ink} />
            </ChromeButton>
          </View>

          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <ChromeButton
              label={_(msg`Previous page`)}
              onPress={() => requestPage(page - 1)}
              disabled={page <= 1}>
              <ChevronLeftIcon size="md" fill={chrome.ink} />
            </ChromeButton>
            {/*
              The position, held to one width so the arrows do not move as it
              changes: `9 / 16` is not the same string as `10 / 16`, and a pager
              whose buttons shift under the reader's hand on the page before they
              press is a pager read by luck.
            */}
            <View style={[a.align_center, {minWidth: 56}]}>
              <Text style={[a.text_xs, a.font_bold, {color: chrome.ink}]}>
                {page} / {totalPages}
              </Text>
            </View>
            {/*
              Forward, through the door — and therefore *this* press rather than a
              page number incremented: `stepForward` is what asks a page's questions
              before letting the reader past them, and an arrow that went round it
              would make the door optional. The same press as a flick to the left, so
              the gesture cannot become the way around it.

              It is the paged reader's step, so it stands down in the scroll, where
              the scroll itself is the way on and a page's questions are met by
              arriving at them (`#/lib/document/quizGate`). Backwards is never gated:
              it is a page already read.
            */}
            <ChromeButton
              label={forwardLabel}
              onPress={stepForward}
              disabled={!canStepForward}>
              <ChevronRightIcon size="md" fill={chrome.ink} />
            </ChromeButton>
          </View>
        </View>
      )}

      {/*
        The stop between a page half-written and everything that would leave it.

        It names the page rather than asking "are you sure?", because the author
        pressed a control whose meaning they already know: what they cannot know is
        which page the words are on, since the card below them says "Page 3" and the
        page they write on moves as they read. What is lost is the card's own draft —
        "Confirm" is the only thing that writes a page — so the message says where it
        lives, and the answer is a choice between those words and the turn that was
        asked for. Cancelling (or Escape) puts the reader back exactly where it was,
        card and all, because nothing has moved yet: the door is *held*, not taken.
      */}
      <StudioConfirmDialog
        control={discardControl}
        title={_(msg`Leave page ${editorPage ?? page} unfinished?`)}
        message={_(
          msg`You have not added the questions and audio on page ${editorPage ?? page} to the document yet. They are only in the card you are writing in, so leaving now loses them.`,
        )}
        confirmLabel={_(msg`Discard and leave`)}
        onConfirm={discardDraft}
      />
    </div>
  )
}
